// Stage 73.20dk (Phase 1) — Tax Slab Master admin page.
//
// Route: /masters/tax-slabs
// RBAC: tenant_admin / super_admin / payroll_admin / hr_admin
//
// FY + Regime keyed. Only ONE active row per (tenant, FY, regime) — the
// backend supersedes any existing active row on create.

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import MainLayout from '../components/MainLayout';
import { Plus, Save, X, Percent, Calendar, Layers, Trash2, RotateCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EMPTY_SLAB_ROW = () => ({ slab_from: 0, slab_to: 0, tax_rate: 0 });
const EMPTY_FORM = () => ({
  financial_year: '',
  regime: 'new',
  slabs: [EMPTY_SLAB_ROW()],
  standard_deduction: 75000,
  rebate_limit: 1200000,
  rebate_max_amount: 60000,
  cess_rate: 4.0,
  surcharge_slabs: [],
  is_active: true,
  notes: '',
});

const REGIME_OPTIONS = [
  { value: 'new', label: 'New Regime' },
  { value: 'old', label: 'Old Regime' },
];

const inr = (n) => Number(n || 0).toLocaleString('en-IN');

export default function TaxSlabMaster() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterFY, setFilterFY] = useState('');
  const [filterRegime, setFilterRegime] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM());
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);


  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filterFY) q.set('financial_year', filterFY);
      if (filterRegime) q.set('regime', filterRegime);
      if (!showInactive) q.set('is_active', 'true');
      const r = await axios.get(`${API_URL}/tax-slab-master/?${q.toString()}`, { withCredentials: true });
      setRows(r.data || []);
    } catch (e) {
      toast.error('Failed to load tax slabs');
    } finally {
      setLoading(false);
    }
  }, [filterFY, filterRegime, showInactive]);

  useEffect(() => { fetchRows(); }, [filterFY, filterRegime, showInactive, fetchRows]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM());
    setShowForm(true);
  };

  const openEdit = (row) => {
    setEditingId(row._id);
    setForm({
      financial_year: row.financial_year,
      regime: row.regime,
      slabs: row.slabs || [EMPTY_SLAB_ROW()],
      standard_deduction: row.standard_deduction || 0,
      rebate_limit: row.rebate_limit || 0,
      rebate_max_amount: row.rebate_max_amount || 0,
      cess_rate: row.cess_rate || 4.0,
      surcharge_slabs: row.surcharge_slabs || [],
      is_active: !!row.is_active,
      notes: row.notes || '',
    });
    setShowForm(true);
  };

  const addSlabRow = () => setForm((f) => ({ ...f, slabs: [...f.slabs, EMPTY_SLAB_ROW()] }));
  const removeSlabRow = (i) => setForm((f) => ({ ...f, slabs: f.slabs.filter((_, idx) => idx !== i) }));
  const updateSlab = (i, k, v) => setForm((f) => {
    const s = [...f.slabs];
    s[i] = { ...s[i], [k]: k === 'tax_rate' ? Number(v) : Number(v) };
    return { ...f, slabs: s };
  });

  const addSurcharge = () => setForm((f) => ({
    ...f, surcharge_slabs: [...f.surcharge_slabs, { from_amount: 0, to_amount: 0, rate: 0 }],
  }));
  const removeSurcharge = (i) => setForm((f) => ({
    ...f, surcharge_slabs: f.surcharge_slabs.filter((_, idx) => idx !== i),
  }));
  const updateSurcharge = (i, k, v) => setForm((f) => {
    const s = [...f.surcharge_slabs];
    s[i] = { ...s[i], [k]: Number(v) };
    return { ...f, surcharge_slabs: s };
  });

  const validate = () => {
    if (!/^\d{4}-\d{2}$/.test(form.financial_year)) {
      toast.error('Financial Year must be in the format YYYY-YY (e.g. 2025-26).');
      return false;
    }
    if (!form.slabs.length) { toast.error('At least one slab is required.'); return false; }
    for (let i = 0; i < form.slabs.length; i += 1) {
      const s = form.slabs[i];
      if (s.slab_to <= s.slab_from) {
        toast.error(`Slab #${i + 1}: "to" must be greater than "from".`);
        return false;
      }
    }
    return true;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API_URL}/tax-slab-master/${editingId}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/tax-slab-master/`, form, { withCredentials: true });
      }
      toast.success('Saved successfully.');
      setShowForm(false);
      await fetchRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`Soft-delete FY ${row.financial_year} · ${row.regime.toUpperCase()} slab?`)) return;
    try {
      await axios.delete(`${API_URL}/tax-slab-master/${row._id}`, { withCredentials: true });
      toast.success('Deactivated.');
      await fetchRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const seedDefaults = async () => {
    setSeeding(true);
    try {
      const r = await axios.post(`${API_URL}/tax-slab-master/seed-defaults`, {}, { withCredentials: true });
      toast.success(`Inserted ${r.data.inserted}, skipped ${r.data.skipped}.`);
      await fetchRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Seeding failed');
    } finally {
      setSeeding(false);
    }
  };

  return (
    <MainLayout>
      <div data-testid="tax-slab-master-page" className="space-y-5">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Layers className="w-6 h-6 text-[#0052FF]" /> Tax Slab Master
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Financial Year + Regime keyed. Only ONE active slab per FY-Regime.
              TDS engine, tax-sheet PDF, and Form 16 all consult this master.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="seed-defaults-btn"
              onClick={seedDefaults}
              disabled={seeding}
              className="flex items-center gap-1.5 border border-slate-300 hover:bg-slate-50 text-slate-700 px-3 py-2 rounded-md text-sm font-medium disabled:opacity-60"
            >
              <RotateCw className={`w-4 h-4 ${seeding ? 'animate-spin' : ''}`} /> Seed India Defaults
            </button>
            <button
              data-testid="add-tax-slab-btn"
              onClick={openCreate}
              className="flex items-center gap-1.5 bg-[#0052FF] hover:bg-[#0040CC] text-white px-3 py-2 rounded-md text-sm font-medium"
            >
              <Plus className="w-4 h-4" /> Add Slab
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs text-slate-500 block mb-1">Financial Year</label>
            <input
              data-testid="filter-fy"
              type="text"
              placeholder="e.g. 2025-26"
              value={filterFY}
              onChange={(e) => setFilterFY(e.target.value)}
              className="border border-slate-300 rounded px-2 py-1 text-sm w-36"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 block mb-1">Regime</label>
            <select
              data-testid="filter-regime"
              value={filterRegime}
              onChange={(e) => setFilterRegime(e.target.value)}
              className="border border-slate-300 rounded px-2 py-1 text-sm"
            >
              <option value="">All</option>
              <option value="new">New</option>
              <option value="old">Old</option>
            </select>
          </div>
          {(filterFY || filterRegime) && (
            <button
              onClick={() => { setFilterFY(''); setFilterRegime(''); }}
              className="text-xs text-slate-500 hover:text-slate-800 underline"
            >
              Clear
            </button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <input
              id="show-inactive"
              type="checkbox"
              data-testid="toggle-show-inactive"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            <label htmlFor="show-inactive" className="text-xs text-slate-600">Show inactive</label>
          </div>
        </div>

        {/* List */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2 font-semibold text-slate-700">FY / AY</th>
                <th className="text-left px-4 py-2 font-semibold text-slate-700">Regime</th>
                <th className="text-left px-4 py-2 font-semibold text-slate-700">Slabs</th>
                <th className="text-right px-4 py-2 font-semibold text-slate-700">Std Ded</th>
                <th className="text-right px-4 py-2 font-semibold text-slate-700">Rebate ≤ / Max</th>
                <th className="text-right px-4 py-2 font-semibold text-slate-700">Cess</th>
                <th className="text-left px-4 py-2 font-semibold text-slate-700">Status</th>
                <th className="text-right px-4 py-2 font-semibold text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="text-center py-8 text-slate-400">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={8} className="text-center py-8 text-slate-400">
                  No tax slabs configured. Click <b>Seed India Defaults</b> to bootstrap FY 2024-25 / 2025-26 / 2026-27.
                </td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr key={r._id} data-testid={`tax-slab-row-${r._id}`} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2">
                    <div className="font-medium text-slate-800">{r.financial_year}</div>
                    <div className="text-xs text-slate-500">AY {r.assessment_year}</div>
                  </td>
                  <td className="px-4 py-2 capitalize">{r.regime}</td>
                  <td className="px-4 py-2 text-xs text-slate-600">
                    {(r.slabs || []).map((s, i) => (
                      <div key={i}>
                        {inr(s.slab_from)} – {s.slab_to >= 99999999 ? '∞' : inr(s.slab_to)} @ {s.tax_rate}%
                      </div>
                    ))}
                  </td>
                  <td className="px-4 py-2 text-right">₹{inr(r.standard_deduction)}</td>
                  <td className="px-4 py-2 text-right">₹{inr(r.rebate_limit)} / ₹{inr(r.rebate_max_amount)}</td>
                  <td className="px-4 py-2 text-right">{r.cess_rate}%</td>
                  <td className="px-4 py-2">
                    {r.is_active
                      ? <span className="inline-flex items-center px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs">Active</span>
                      : <span className="inline-flex items-center px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full text-xs">Inactive</span>}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      data-testid={`edit-tax-slab-${r._id}`}
                      onClick={() => openEdit(r)}
                      className="text-[#0052FF] hover:underline text-xs mr-3"
                    >
                      Edit
                    </button>
                    <button
                      data-testid={`delete-tax-slab-${r._id}`}
                      onClick={() => onDelete(r)}
                      className="text-red-600 hover:underline text-xs"
                    >
                      Deactivate
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Create / Edit Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="tax-slab-modal">
            <div className="bg-white rounded-lg w-full max-w-3xl shadow-lg max-h-[92vh] overflow-y-auto">
              <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white z-10">
                <h2 className="text-lg font-semibold text-slate-800">
                  {editingId ? 'Edit Tax Slab' : 'Add Tax Slab'}
                </h2>
                <button onClick={() => setShowForm(false)} data-testid="close-modal-btn">
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
              <div className="p-5 space-y-4">
                {/* Top row: FY + Regime + Active */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-slate-500 flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" /> Financial Year
                    </label>
                    <input
                      data-testid="form-fy"
                      type="text"
                      value={form.financial_year}
                      onChange={(e) => setForm((f) => ({ ...f, financial_year: e.target.value }))}
                      placeholder="2025-26"
                      className="w-full border border-slate-300 rounded px-2 py-1 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Regime</label>
                    <select
                      data-testid="form-regime"
                      value={form.regime}
                      onChange={(e) => setForm((f) => ({ ...f, regime: e.target.value }))}
                      className="w-full border border-slate-300 rounded px-2 py-1 text-sm"
                    >
                      {REGIME_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </div>
                  <div className="flex items-center gap-2 pt-4">
                    <input
                      id="active"
                      type="checkbox"
                      data-testid="form-active"
                      checked={form.is_active}
                      onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                    />
                    <label htmlFor="active" className="text-sm text-slate-700">Active</label>
                  </div>
                </div>

                {/* Metadata numbers */}
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <label className="text-xs text-slate-500">Standard Deduction</label>
                    <input
                      type="number"
                      data-testid="form-std-deduction"
                      value={form.standard_deduction}
                      onChange={(e) => setForm((f) => ({ ...f, standard_deduction: Number(e.target.value) }))}
                      className="w-full border border-slate-300 rounded px-2 py-1 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Rebate limit (87A)</label>
                    <input
                      type="number"
                      data-testid="form-rebate-limit"
                      value={form.rebate_limit}
                      onChange={(e) => setForm((f) => ({ ...f, rebate_limit: Number(e.target.value) }))}
                      className="w-full border border-slate-300 rounded px-2 py-1 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Rebate max</label>
                    <input
                      type="number"
                      data-testid="form-rebate-max"
                      value={form.rebate_max_amount}
                      onChange={(e) => setForm((f) => ({ ...f, rebate_max_amount: Number(e.target.value) }))}
                      className="w-full border border-slate-300 rounded px-2 py-1 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 flex items-center gap-1">
                      <Percent className="w-3.5 h-3.5" /> Cess %
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      data-testid="form-cess-rate"
                      value={form.cess_rate}
                      onChange={(e) => setForm((f) => ({ ...f, cess_rate: Number(e.target.value) }))}
                      className="w-full border border-slate-300 rounded px-2 py-1 text-sm"
                    />
                  </div>
                </div>

                {/* Slabs */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-slate-800">Slabs</h3>
                    <button
                      data-testid="add-slab-row"
                      onClick={addSlabRow}
                      className="text-xs text-[#0052FF] hover:underline"
                    >
                      + Add slab
                    </button>
                  </div>
                  <div className="space-y-2">
                    {form.slabs.map((s, i) => (
                      <div key={i} data-testid={`slab-row-${i}`} className="grid grid-cols-8 gap-2 items-center">
                        <label className="col-span-1 text-xs text-slate-500">From</label>
                        <input
                          type="number"
                          data-testid={`slab-from-${i}`}
                          value={s.slab_from}
                          onChange={(e) => updateSlab(i, 'slab_from', e.target.value)}
                          className="col-span-2 border border-slate-300 rounded px-2 py-1 text-sm"
                        />
                        <label className="col-span-1 text-xs text-slate-500">To</label>
                        <input
                          type="number"
                          data-testid={`slab-to-${i}`}
                          value={s.slab_to}
                          onChange={(e) => updateSlab(i, 'slab_to', e.target.value)}
                          className="col-span-2 border border-slate-300 rounded px-2 py-1 text-sm"
                        />
                        <input
                          type="number"
                          step="0.01"
                          data-testid={`slab-rate-${i}`}
                          value={s.tax_rate}
                          onChange={(e) => updateSlab(i, 'tax_rate', e.target.value)}
                          className="col-span-1 border border-slate-300 rounded px-2 py-1 text-sm"
                          placeholder="Rate %"
                        />
                        <button
                          data-testid={`remove-slab-${i}`}
                          onClick={() => removeSlabRow(i)}
                          className="text-red-600 hover:text-red-700"
                          title="Remove"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Surcharge */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-slate-800">Surcharge slabs (optional)</h3>
                    <button
                      data-testid="add-surcharge-row"
                      onClick={addSurcharge}
                      className="text-xs text-[#0052FF] hover:underline"
                    >
                      + Add surcharge
                    </button>
                  </div>
                  {form.surcharge_slabs.map((s, i) => (
                    <div key={i} data-testid={`surcharge-row-${i}`} className="grid grid-cols-8 gap-2 items-center mb-2">
                      <label className="col-span-1 text-xs text-slate-500">From</label>
                      <input
                        type="number" value={s.from_amount}
                        onChange={(e) => updateSurcharge(i, 'from_amount', e.target.value)}
                        className="col-span-2 border border-slate-300 rounded px-2 py-1 text-sm"
                      />
                      <label className="col-span-1 text-xs text-slate-500">To</label>
                      <input
                        type="number" value={s.to_amount}
                        onChange={(e) => updateSurcharge(i, 'to_amount', e.target.value)}
                        className="col-span-2 border border-slate-300 rounded px-2 py-1 text-sm"
                      />
                      <input
                        type="number" step="0.01" value={s.rate}
                        onChange={(e) => updateSurcharge(i, 'rate', e.target.value)}
                        className="col-span-1 border border-slate-300 rounded px-2 py-1 text-sm"
                        placeholder="Rate %"
                      />
                      <button
                        onClick={() => removeSurcharge(i)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>

                <div>
                  <label className="text-xs text-slate-500">Notes</label>
                  <textarea
                    data-testid="form-notes"
                    value={form.notes}
                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                    rows={2}
                    className="w-full border border-slate-300 rounded px-2 py-1 text-sm"
                  />
                </div>
              </div>
              <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
                <button
                  onClick={() => setShowForm(false)}
                  className="px-3 py-1.5 border border-slate-300 rounded text-sm hover:bg-slate-50"
                >Cancel</button>
                <button
                  data-testid="save-tax-slab-btn"
                  onClick={save}
                  disabled={saving}
                  className="flex items-center gap-1 bg-[#0052FF] hover:bg-[#0040CC] text-white px-4 py-1.5 rounded text-sm font-medium disabled:opacity-60"
                >
                  <Save className="w-4 h-4" /> Save
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
